import { useState } from "react";

export default function ChatInput({ onSend }) {
  const [value, setValue] = useState("");

  const maxLines = 10;
  const lineHeightPx = 24; // must match innerInputStyle lineHeight
  const verticalPaddingPx = 20; // paddingTop(10) + paddingBottom(10)
  const maxTextareaHeight = maxLines * lineHeightPx + verticalPaddingPx;

  const submit = (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    onSend(v);
    setValue("");
  };

  const inputWrapStyle = {
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 10,
    position: 'relative',
    background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51, 51, 51, 0.79), rgba(153, 153, 153, 0.30)',
    backgroundBlendMode: 'linear_dodge, luminosity, darken',
    boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
    overflow: 'hidden',
    borderRadius: 24,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 10,
    display: 'inline-flex',
  };

  // const overlayPanelStyle = {
  //   position: 'absolute',
  //   left: -3,
  //   top: -4,
  //   width: '98%',
  //   height: 49,
  //   background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51, 51, 51, 0.79), rgba(153, 153, 153, 0.30)',
  //   backgroundBlendMode: 'linear_dodge, luminosity, darken',
  //   boxShadow: '0px 0px 16px #F2F2F2 inset',
  //   backdropFilter: 'blur(3px)',
  //   WebkitBackdropFilter: 'blur(3px)',
  //   borderRadius: 24,
  //   pointerEvents: 'none',
  // };

  const inputStyle = {
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 10,
    background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51, 51, 51, 0.79), rgba(153, 153, 153, 0.30)',
    backgroundBlendMode: 'linear_dodge, luminosity, darken',
    boxShadow: 'rgb(242, 242, 242) 0px 0px 3px 0px inset, rgba(255, 255, 255, 0.5) 0px 0px 3px 0px inset, rgb(255, 255, 255) -1px -1px 0.5px -1px inset, rgb(255, 255, 255) 1px 1px 0.5px -1px inset, rgb(38, 38, 38) -1px -1px 0px -0.5px inset, rgb(51, 51, 51) 1px 1px 0px -0.5px inset',
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  };

  const innerInputStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    backgroundColor: 'transparent',
    fontSize: 16,
    lineHeight: '24px',
    fontFamily:
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Helvetica, Arial, sans-serif',
    resize: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  const sendButtonStyle = {
    width: 42,
    height: 42,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 10,
    paddingBottom: 10,
    background: 'transparent',
    backgroundBlendMode: 'linear_dodge, luminosity, darken',
    boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 10,
    display: 'inline-flex',
    border: 'none',
    cursor: 'pointer',
  };

  const handleInput = (e) => {
    const el = e.target;
    // Reset height to measure natural scrollHeight, then clamp to max
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, maxTextareaHeight);
    el.style.height = newHeight + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevent newline and submit the form
      e.preventDefault();
      submit(e);
    }
  };

  return (
    <form onSubmit={submit} className="relative" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={inputStyle}>
          <textarea
            rows={1}
            placeholder="Type a message…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            style={{
              ...innerInputStyle,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 10,
              paddingBottom: 10,
              // Start small and grow up to 10 lines, then scroll vertically
              height: 'auto',
              maxHeight: maxTextareaHeight,
            }}
          />
        </div>
        <button type="submit" style={sendButtonStyle} aria-label="Send">
          <svg width="24" height="23" viewBox="0 0 24 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M11.1968 12.3031L2.52577 9.41273C2.02936 9.24694 1.6949 8.78119 1.69777 8.25794C1.70065 7.73469 2.03894 7.27181 2.53727 7.11177L20.7197 1.25635C21.1519 1.11739 21.6263 1.23144 21.9474 1.55248C22.2684 1.87352 22.3824 2.34789 22.2435 2.7801L16.3881 20.9626C16.228 21.4609 15.7651 21.7992 15.2419 21.8021C14.7186 21.8049 14.2529 21.4705 14.0871 20.9741L11.1968 12.3031ZM3.63648 8.26752L11.9922 11.0534C12.2069 11.1243 12.3755 11.293 12.4464 11.5076L15.2323 19.8634L20.7399 2.75998L3.63648 8.26752Z" fill="white"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M12.2728 12.2427C11.993 12.5235 11.5368 12.5235 11.257 12.2427C10.9762 11.9629 10.9762 11.5067 11.257 11.2269L20.6496 1.83425C20.9294 1.55346 21.3856 1.55346 21.6654 1.83425C21.9462 2.11408 21.9462 2.57025 21.6654 2.85008L12.2728 12.2427Z" fill="white"/>
          </svg>
        </button>
      </div>
    </form>
  );
}